import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Menu,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SaveIcon from "@mui/icons-material/Save";
import TableChartIcon from "@mui/icons-material/TableChart";
import {
  deleteProspectingCompanyProfileDocument,
  downloadProspectingCompanyProfileDocument,
  uploadProspectingCompanyProfileDocument,
} from "./ProspectingCompanyAPI";
import { downloadBlobFile } from "./ProspectingCompaniesHelpers";
import SectionLabel from "../../../shared/components/SectionLabel";
import CompanyDocumentsPanel from "../../../shared/components/CompanyDocumentsPanel";
import { PRIMARY_BUTTON_SX } from "../../../theme/buttonstyles";

const PROFILE_DOCUMENT_LABELS = {
  description: "Upload one or more PDF prospect profiles for this company.",
  title: "Prospect Profiles",
  emptyMessage: "No prospect profiles uploaded yet.",
  invalidTypeMessage: "Prospect profile document must be uploaded as a PDF.",
  uploadErrorMessage: "Failed to upload prospect profile document.",
  downloadErrorMessage: "Failed to download prospect profile document.",
  viewErrorMessage: "Failed to open prospect profile document.",
  deleteErrorMessage: "Failed to delete prospect profile document.",
  previewTitle: "Loading prospect profile document...",
  defaultFileName: "prospect-profile.pdf",
  deleteButtonLabel: "Delete",
};

function ProspectingCompanyProfileDocumentsTab({
  companyId,
  company,
  onFieldChange,
  onSaveProfileDetails,
  onCompanyUpdated,
}) {
  const [detailsExpanded, setDetailsExpanded] = useState(true);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const safeString = (v) => (typeof v === "string" ? v : "");
  const exportMenuOpen = Boolean(exportAnchorEl);

  const profileDetailRows = [
    ["Plan For Change", safeString(company.plan_for_change)],
    ["Location Drivers", safeString(company.location_drivers)],
    ["Time Frame", safeString(company.time_frame)],
    ["Misc Lead Notes", safeString(company.misc_lead_notes)],
    ["Site Use", safeString(company.site_use)],
    ["New Facility Size", safeString(company.new_facility_size)],
    ["New Facility Needs", safeString(company.new_facility_needs)],
    ["Utility Requirements", safeString(company.utility_requirements)],
    ["Transportation Requirements", safeString(company.transportation_requirements)],
    ["Number Of Initial Jobs", safeString(company.number_of_initial_jobs)],
    ["Will More Jobs Be Added", safeString(company.will_more_jobs_be_added)],
    ["Positions & Required Skills", safeString(company.positions_required_skills)],
    ["Typical Pay Ranges", safeString(company.typical_pay_ranges)],
  ];

  const exportFileBaseName = `prospect-profile-${safeString(company.name) || companyId || "details"}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  const openExportMenu = (event) => {
    event.stopPropagation();
    setExportAnchorEl(event.currentTarget);
  };

  const closeExportMenu = (event) => {
    event?.stopPropagation?.();
    setExportAnchorEl(null);
  };

  const exportProfileExcel = async () => {
    const XLSX = await import("xlsx");
    const worksheet = XLSX.utils.json_to_sheet(
      profileDetailRows.map(([field, value]) => ({
        Field: field,
        Value: value || "",
      })),
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Prospect Profile");
    XLSX.writeFile(workbook, `${exportFileBaseName}.xlsx`);
  };

  const exportProfilePdf = async () => {
    const { default: jsPDF } = await import("jspdf");
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 48;
    const contentWidth = pageWidth - margin * 2;
    let y = margin;

    const addPageIfNeeded = (heightNeeded) => {
      if (y + heightNeeded <= pageHeight - margin) return;
      doc.addPage();
      y = margin;
    };

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("Prospect Profile Details", margin, y);
    y += 24;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    if (company.name) {
      doc.text(`Company: ${company.name}`, margin, y);
      y += 18;
    }
    doc.text(`Exported: ${new Date().toLocaleDateString()}`, margin, y);
    y += 26;

    profileDetailRows.forEach(([field, value]) => {
      const text = value || "";
      const valueLines = doc.splitTextToSize(text || " ", contentWidth);
      const blockHeight = 18 + Math.max(valueLines.length, 1) * 14 + 12;
      addPageIfNeeded(blockHeight);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text(field, margin, y);
      y += 16;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(valueLines, margin, y);
      y += Math.max(valueLines.length, 1) * 14 + 12;
    });

    doc.save(`${exportFileBaseName}.pdf`);
  };

  const handleExport = async (format, event) => {
    closeExportMenu(event);
    if (format === "pdf") {
      await exportProfilePdf();
      return;
    }
    await exportProfileExcel();
  };

  return (
    <Stack spacing={2}>
      <CompanyDocumentsPanel
        companyId={companyId}
        documents={company?.profile_documents}
        uploadedAt={company?.profile_document_uploaded_at}
        labels={PROFILE_DOCUMENT_LABELS}
        onUpload={uploadProspectingCompanyProfileDocument}
        onDownload={downloadProspectingCompanyProfileDocument}
        onDelete={deleteProspectingCompanyProfileDocument}
        onCompanyUpdated={onCompanyUpdated}
        downloadBlobFile={downloadBlobFile}
      />

      <Accordion
        expanded={detailsExpanded}
        onChange={(_event, expanded) => setDetailsExpanded(expanded)}
        sx={{
          mt: 1,
          mb: 0,
          "&:before": {
            display: "none",
          },
          "&.Mui-expanded": {
            mt: 1,
            mb: 0,
          },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              width: "100%",
              pr: 1,
            }}
          >
            <SectionLabel>Prospect Profile Details</SectionLabel>

            {detailsExpanded && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={openExportMenu}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderColor: "rgba(0,51,108,0.28)",
                  color: "#00336C",
                  "&:hover": {
                    borderColor: "#00336C",
                    backgroundColor: "rgba(0,51,108,0.04)",
                  },
                }}
              >
                Export
              </Button>
            )}
          </Box>
        </AccordionSummary>

        <Menu
          anchorEl={exportAnchorEl}
          open={exportMenuOpen}
          onClose={closeExportMenu}
          onClick={(event) => event.stopPropagation()}
        >
          <MenuItem onClick={(event) => handleExport("pdf", event)}>
            <PictureAsPdfIcon sx={{ mr: 1.25, color: "#B42318" }} />
            Export PDF
          </MenuItem>
          <MenuItem onClick={(event) => handleExport("excel", event)}>
            <TableChartIcon sx={{ mr: 1.25, color: "#648315" }} />
            Export Excel
          </MenuItem>
        </Menu>

        <AccordionDetails>
          <Stack spacing={2}>
            <TextField
              label="Plan For Change"
              value={safeString(company.plan_for_change)}
              onChange={onFieldChange("plan_for_change")}
              fullWidth
              multiline
              minRows={2}
            />

            <TextField
              label="Location Drivers"
              value={safeString(company.location_drivers)}
              onChange={onFieldChange("location_drivers")}
              fullWidth
              multiline
              minRows={2}
            />

            <TextField
              label="Time Frame"
              value={safeString(company.time_frame)}
              onChange={onFieldChange("time_frame")}
              fullWidth
            />

            <TextField
              label="Misc Lead Notes"
              value={safeString(company.misc_lead_notes)}
              onChange={onFieldChange("misc_lead_notes")}
              fullWidth
              multiline
              minRows={2}
            />

            <TextField
              label="Site Use"
              value={safeString(company.site_use)}
              onChange={onFieldChange("site_use")}
              fullWidth
            />

            <TextField
              label="New Facility Size"
              value={safeString(company.new_facility_size)}
              onChange={onFieldChange("new_facility_size")}
              fullWidth
            />

            <TextField
              label="New Facility Needs"
              value={safeString(company.new_facility_needs)}
              onChange={onFieldChange("new_facility_needs")}
              fullWidth
            />

            <TextField
              label="Utility Requirements"
              value={safeString(company.utility_requirements)}
              onChange={onFieldChange("utility_requirements")}
              fullWidth
            />

            <TextField
              label="Transportation Requirements"
              value={safeString(company.transportation_requirements)}
              onChange={onFieldChange("transportation_requirements")}
              fullWidth
            />

            <TextField
              label="Number Of Initial Jobs"
              value={safeString(company.number_of_initial_jobs)}
              onChange={onFieldChange("number_of_initial_jobs")}
              fullWidth
            />

            <TextField
              label="Will More Jobs Be Added"
              value={safeString(company.will_more_jobs_be_added)}
              onChange={onFieldChange("will_more_jobs_be_added")}
              fullWidth
            />

            <TextField
              label="Positions & Required Skills"
              value={safeString(company.positions_required_skills)}
              onChange={onFieldChange("positions_required_skills")}
              fullWidth
            />

            <TextField
              label="Typical Pay Ranges"
              value={safeString(company.typical_pay_ranges)}
              onChange={onFieldChange("typical_pay_ranges")}
              fullWidth
            />

            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={onSaveProfileDetails}
              sx={PRIMARY_BUTTON_SX}
            >
              Save Changes
            </Button>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}

export default ProspectingCompanyProfileDocumentsTab;
